import './TrackmanParametersList.scss';

import { Input, Select, Table, TableHead, TableRow, TableCell, TableBody } from '@trackman/web-shared-components';
import HeaderCreateButton from 'components/Admin/HeaderCreateButton/HeaderCreateButton';
import DeleteListItemModal from 'components/Admin/Modals/DeleteListItemModal/DeleteListItemModal';
import SortOrderButton from 'components/SortOrderButton/SortOrderButton';
import Paragraph from 'components/TextComps/Paragraph';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdminPagesRelativeRoutes, DefaultRoutes } from 'Routes';
import { AdminParameterListItemModel, ParametersListEntry } from 'types/admin/trackmanParameters';
import { ValidSortProp } from 'utilities/dataSorters';
import useSetPageTitle from 'utilities/useSetPageTitle';
import { ReactComponent as SearchIcon } from 'images/icons/Search.svg';
import { ReactComponent as PencilIcon } from 'images/icons/Pencil.svg';
import { ReactComponent as TrashIcon } from 'images/icons/Trash.svg';
import { useServices } from 'index';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getFilteredParametersList, getUniquePrimaryCategories } from 'utilities/filterTrackmanParameters';
import { useParametersFiltersStore } from 'stores/Admin/Editors/ParametersFiltersStore';

export const TrackmanParametersList = () => {
  useSetPageTitle({ title: 'Trackman Parameters List' });
  const navigate = useNavigate();
  const parametersService = useServices().parametersService;

  const [isModalOn, setIsModalOn] = useState(false);
  const [sortByProperty, setSortByProperty] = useState<ValidSortProp<AdminParameterListItemModel>>('title');
  const [sortOrder, setSortOrder] = useState<'ASCENDING' | 'DESCENDING'>('ASCENDING');
  const [selectedParameterId, setSelectedParameterId] = useState('');

  const filterByName = useParametersFiltersStore((state) => state.filterByName);
  const filterByCategory = useParametersFiltersStore((state) => state.filterByCategory);
  const filterByVisibility = useParametersFiltersStore((state) => state.filterByVisibility);
  const setFilterByName = useParametersFiltersStore((state) => state.setFilterByName);
  const setFilterByCategory = useParametersFiltersStore((state) => state.setFilterByCategory);
  const setFilterByVisibility = useParametersFiltersStore((state) => state.setFilterByVisibility);

  const queryClient = useQueryClient();

  const { mutate: deleteParameter, isPending: isDeletePending } = useMutation({
    mutationFn: (id: string) => parametersService.deleteParameter(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminParametersList'] });
      setIsModalOn(false);
    },
  });

  const { data, isPending } = useQuery({
    queryKey: ['adminParametersList'],
    queryFn: parametersService.getParameters,
    refetchOnWindowFocus: false,
  });

  const uniquePrimaryCategories = getUniquePrimaryCategories(data ?? []);

  const filteredParametersList: ParametersListEntry[] | undefined = data
    ? getFilteredParametersList(data, sortByProperty, sortOrder, filterByName, filterByCategory, filterByVisibility)
    : undefined;

  const handleSortChange = (property: ValidSortProp<AdminParameterListItemModel>, order: 'ASCENDING' | 'DESCENDING') => {
    setSortByProperty(property);
    setSortOrder(order);
  };

  return (
    <>
      <div className='default-page-header'>
        <h1 className='page-title'>Trackman Parameters List</h1>
      </div>
      <div className='admin-content-container'>
        <div className='parameters-list-header'>
          <div className='parameters-searchbar'>
            <div className='parameters-search parameters-search--input'>
              <Input placeholder='Search by name' value={filterByName} onChange={(e) => setFilterByName(e.currentTarget.value)} />
              <SearchIcon />
            </div>

            <div className='parameters-search'>
              <Select
                label='Select primary category'
                value={filterByCategory}
                selectOptions={uniquePrimaryCategories.map((category) => {
                  return {
                    label: category,
                    value: category,
                  };
                })}
                onChange={(e) => setFilterByCategory(e.target.value as string)}
              />
            </div>

            <div className='parameters-search'>
              <Select
                label='Select visibility status'
                value={filterByVisibility}
                selectOptions={['None', 'Yes', 'No'].map((status) => {
                  return {
                    label: status,
                    value: status,
                  };
                })}
                onChange={(e) => setFilterByVisibility(e.target.value as string)}
              />
            </div>
          </div>
          <HeaderCreateButton
            buttonTitle='New parameter'
            onClick={() => navigate(`${DefaultRoutes.Admin}/${AdminPagesRelativeRoutes.TrackmanParametersEditor}`)}
          />
        </div>

        <div className='tmu-table-wrapper'>
          <Table className='tmu-table admin-table'>
            <TableHead>
              <TableRow>
                <TableCell>
                  Name
                  <SortOrderButton
                    isActive={sortByProperty === 'title'}
                    sortOrder={sortOrder}
                    onSortChange={(sortOrder) => handleSortChange('title', sortOrder)}
                  />
                </TableCell>
                <TableCell>Languages</TableCell>
                <TableCell>
                  Primary Category
                  <SortOrderButton
                    isActive={sortByProperty === 'primaryCategory'}
                    sortOrder={sortOrder}
                    onSortChange={(sortOrder) => handleSortChange('primaryCategory', sortOrder)}
                  />
                </TableCell>
                <TableCell>
                  Visible
                  <SortOrderButton
                    isActive={sortByProperty === 'visible'}
                    sortOrder={sortOrder}
                    onSortChange={(sortOrder) => handleSortChange('visible', sortOrder)}
                  />
                </TableCell>
                <TableCell>
                  Date created
                  <SortOrderButton
                    isActive={sortByProperty === 'createdAt'}
                    sortOrder={sortOrder}
                    onSortChange={(sortOrder) => handleSortChange('createdAt', sortOrder)}
                  />
                </TableCell>
                <TableCell>
                  Last edited
                  <SortOrderButton
                    isActive={sortByProperty === 'updatedAt'}
                    sortOrder={sortOrder}
                    onSortChange={(sortOrder) => handleSortChange('updatedAt', sortOrder)}
                  />
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredParametersList && filteredParametersList.length ? (
                filteredParametersList.map((row) => {
                  return (
                    <TableRow hover tabIndex={-1} key={row.id}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell className='languages'>{row.languages}</TableCell>
                      <TableCell>{row.primaryCategory}</TableCell>
                      <TableCell>{row.visible}</TableCell>
                      <TableCell>{row.createdAt}</TableCell>
                      <TableCell>{row.updatedAt}</TableCell>
                      <TableCell>
                        <div className='action-buttons'>
                          <Link to={`/Admin/${AdminPagesRelativeRoutes.TrackmanParametersEditor}/${row.id}`} className='row-button'>
                            <PencilIcon />
                          </Link>
                          <button
                            className={`row-button ${isDeletePending ? 'disabled' : ''}`}
                            onClick={() => {
                              setSelectedParameterId(row.id);
                              setIsModalOn(true);
                            }}
                            disabled={isDeletePending}
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Paragraph>
                      <>No parameters found</>
                    </Paragraph>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <DeleteListItemModal
          isModalOn={isModalOn}
          setIsModalOn={setIsModalOn}
          deleteAction={() => deleteParameter(selectedParameterId)}
          itemName='Trackman Parameter'
        />
      </div>
    </>
  );
};
